import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TableHeaderInterfaceModel } from '../../../../models/table.model';
import { UserAreaModel } from '../../../../models/user-area.model';
import { UserModel, UserType } from '../../../../models/user.model';
import { UserAreaService } from '../../../../services/user-area.service';
import { UserService } from '../../../../services/user.service';
import { translateUserType } from '../../../../utils/language.utils';
import { getTableNameValue } from '../../../../utils/user.utils';
import {
  Filter,
  TableColumn,
} from '../../../shared-components/custom-table/custom-table.component';

type ContactTableRow = UserModel & {
  areaIds: number[];
  areaNames: string[];
};

@Component({
  selector: 'app-contacts',
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.scss',
  standalone: false,
})
export class ContactsComponent {
  public dataSource$: Observable<ContactTableRow[]> = of([]);
  public usersLoading = true;
  public cardSlides: TableHeaderInterfaceModel[] = [];
  private users: ContactTableRow[] = [];
  public filters: Filter[] = [];

  constructor(
    private userService: UserService,
    private userAreaService: UserAreaService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.getUsers();
  }

  tableHeaderDropdown = {
    label: 'Kontakt hinzufügen',
    visible: false,
    options: [
      {
        label: 'Dozenten',
        value: UserType.LECTURER,
        command: () => {
          this.router.navigate(['create'], {
            relativeTo: this.activatedRoute,
            queryParams: { type: 'lecturer' },
          });
        },
      },
      // {
      //   label: 'Nutzer',
      //   value: UserType.USER,
      //   command: () => {
      //     this.router.navigate(['create'], {
      //       relativeTo: this.activatedRoute,
      //       queryParams: { type: 'user' },
      //     });
      //   },
      // },
      {
        label: 'Kooperation',
        value: UserType.COOPERATION,
        command: () => {
          this.router.navigate(['create'], {
            relativeTo: this.activatedRoute,
            queryParams: { type: 'cooperation' },
          });
        },
      },
    ],
  };

  private getUsers() {
    forkJoin({
      users: this.userService.getAllUsers(),
      areas: this.userAreaService.getAllAreas(),
    })
      .pipe(
        switchMap(({ users, areas }) => {
          this.initializeAreaFilters(areas);
          if (!areas.length) {
            return of({
              users: users.map(user => ({
                ...user,
                areaIds: [],
                areaNames: [],
              })),
            });
          }

          const membershipRequests = areas.map(area =>
            this.userAreaService.getMemberships(area.id).pipe(
              map(memberships => ({ area, memberships })),
              catchError(error => {
                console.error(
                  `Error fetching memberships for area ${area.id}`,
                  error
                );
                return of({ area, memberships: [] });
              })
            )
          );

          return forkJoin(membershipRequests).pipe(
            map(areaMemberships => {
              const userAreasMap = new Map<
                number,
                { ids: number[]; names: string[] }
              >();

              areaMemberships.forEach(({ area, memberships }) => {
                memberships.forEach(membership => {
                  const currentEntry = userAreasMap.get(membership.user_id) ?? {
                    ids: [],
                    names: [],
                  };

                  if (!currentEntry.ids.includes(area.id)) {
                    currentEntry.ids.push(area.id);
                  }

                  if (!currentEntry.names.includes(area.name)) {
                    currentEntry.names.push(area.name);
                  }

                  userAreasMap.set(membership.user_id, currentEntry);
                });
              });

              return {
                users: users.map(user => {
                  const areas = userAreasMap.get(user.id);
                  return {
                    ...user,
                    areaIds: areas?.ids ?? [],
                    areaNames: areas?.names ?? [],
                  };
                }),
              };
            })
          );
        }),
        map(({ users }) => {
          this.users = users;
          this.dataSource$ = of(users);
        }),
        catchError(error => {
          console.error('Error fetching users', error);
          this.users = [];
          this.dataSource$ = of([]);
          this.filters = [];
          return of(null);
        })
      )
      .subscribe({
        complete: () => {
          this.initHeader();
          this.usersLoading = false;
        },
      });
  }

  private initializeAreaFilters(areas: UserAreaModel[]) {
    this.filters = [
      {
        filteredColumn: 'areaIds',
        type: 'multiselect',
        label: 'Mitgliederbereich',
        placeholder: 'Mitgliederbereich auswählen...',
        selectOptions: areas
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(area => ({
            value: area.id,
            label: area.name,
          })),
      },
    ];
  }

  private initHeader() {
    this.cardSlides = [
      {
        title: 'Alle Kontakte',
        details: this.getUserCountByRole().pipe(
          map(count => (count == 1 ? `${count} Kontakt` : `${count} Kontakte`))
        ),
        titleIcon: 'fa-user',
        selected: true,
      },
      {
        title: 'Dozenten',
        filterString: UserType.LECTURER,
        details: this.getUserCountByRole(UserType.LECTURER).pipe(
          map(count => (count == 1 ? `${count} Kontakt` : `${count} Kontakte`))
        ),
        titleIcon: 'fa-chalkboard-user',
      },
      {
        title: 'Nutzer',
        filterString: UserType.USER,
        details: this.getUserCountByRole(UserType.USER).pipe(
          map(count => (count == 1 ? `${count} Kontakt` : `${count} Kontakte`))
        ),
        titleIcon: 'fa-graduation-cap',
      },
      {
        title: 'Kooperation',
        filterString: UserType.COOPERATION,
        details: this.getUserCountByRole(UserType.COOPERATION).pipe(
          map(count => (count == 1 ? `${count} Kontakt` : `${count} Kontakte`))
        ),
        titleIcon: 'fa-handshake',
      },
    ];
  }

  private getUserCountByRole(type?: UserType): Observable<number> {
    return this.dataSource$.pipe(
      map(users =>
        type ? users.filter(user => user.type === type).length : users.length
      ),
      catchError(error => {
        console.error(
          'Error fetching user count by type from dataSource',
          error
        );
        return of(0);
      })
    );
  }

  private filterUsersByRole(type?: UserType) {
    const users = [...this.users];
    if (!type) {
      this.dataSource$ = of(users);
      return;
    }
    this.dataSource$ = of(users.filter(user => user.type === type));
  }

  public columns: TableColumn[] = [
    {
      columnDef: 'name',
      header: 'Name',
      width: '25%',
      sortable: true,
      filterValue: (row: UserModel) => getTableNameValue(row),
      avatarUser: row => row,
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'email',
      header: 'E-Mail',
      width: '20%',
      sortable: true,
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'phone',
      header: 'Telefon',
      width: '15%',
      fontWeight: '400',
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'type',
      header: 'Typ',
      sortable: true,
      width: '15%',
      fontWeight: '400',
      filterValue: (row: UserModel) => translateUserType(row.type),
      render: (row: UserModel) => translateUserType(row.type),
      onCellClick: this.onCellClick.bind(this),
    },
    {
      columnDef: 'actions',
      header: 'Aktionen',
      headerAlign: 'center',
      cellAlign: 'center',
      width: '10%',
      fontWeight: '400',
      render: () =>
        `<button class="table-action-button"><i class="fa-regular fa-chevron-down"></i></button>`,
      onCellClick: this.onCellClick.bind(this),
    },
  ];

  onCellClick(row: any, column: any) {
    const user = this.users.find(u => u.id === row.id);
    this.router.navigate(['edit', btoa(String(row.id))], {
      relativeTo: this.activatedRoute,
      queryParams: { type: user.type },
    });
  }

  onCardClicked(event: TableHeaderInterfaceModel) {
    this.cardSlides = this.cardSlides.map(slide => ({
      ...slide,
      selected: slide.title === event.title,
    }));

    if (event.title === 'Alle Kontakte' || !event.filterString) {
      this.filterUsersByRole();
    } else {
      this.filterUsersByRole(event.filterString as UserType);
    }
  }
}
